import { ReactNode } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField
} from 'v5/design-system/Form';
import { cn } from 'v5/platform/dom/cn';
import { useFieldContext } from '../FieldContext';
import { RequiredIndicator } from '../RequiredIndicator';
import { fieldAnchor } from '../unitPath';
import type { FieldFrameSpec } from './fieldComponent.types';

export interface FieldControlBaseProps {
  spec: FieldFrameSpec;
  children: ReactNode;
  /** Hide the label from view (units render titles themselves); it still names the control. */
  hideLabel?: boolean;
  /**
   * For a field whose parts are form fields of their own (no `FormField` at
   * its path): shows a problem of the whole value, which no part shows.
   */
  path?: string;
}

/** The message of an error set at exactly `path` (not inside it). */
function WholeValueMessage({ path }: { path: string }) {
  const { getFieldState } = useFormContext();
  const { error } = getFieldState(path, useFormState({ name: path }));
  const message = error?.message;
  if (message === undefined || message === '') return null;
  return <p className="text-base font-medium text-fg-danger">{message}</p>;
}

/**
 * Label + description + control + validation message for one field.
 */
export function FieldControlBase({
  spec,
  children,
  hideLabel = false,
  path
}: FieldControlBaseProps) {
  const ctx = useFieldContext();
  const showRequired = ctx.mode === 'edit' && spec.required;
  const { name } = useFormField();
  return (
    <FormItem {...fieldAnchor(path ?? name)}>
      {spec.label !== '' && (
        <FormLabel
          className={cn(
            // Shown, a label reads like a unit's title beside its semibold value.
            ctx.mode === 'display'
              ? 'font-normal text-fg-muted'
              : 'font-semibold text-fg-default',
            // Hidden, it still names the control.
            hideLabel && 'sr-only'
          )}
        >
          {spec.label}
          {showRequired && <RequiredIndicator />}
        </FormLabel>
      )}
      {spec.description !== null && spec.description.trim() !== '' && (
        <FormDescription className="text-fg-default">
          {spec.description}
        </FormDescription>
      )}
      <FormControl>{children}</FormControl>
      {path === undefined ? <FormMessage /> : <WholeValueMessage path={path} />}
    </FormItem>
  );
}
